import { useNavigate } from 'react-router-dom';

import { formatDate } from 'helpers/dates-formatter';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import type { InsightSummary } from 'containers/user/insights/use-insights';

interface InsightItemProps {
  insight: InsightSummary;
}

const DEFAULT_DESCRIPTION_MAX_LENGTH = 100;

const InsightItem = ({ insight }: InsightItemProps): React.JSX.Element => {
  const navigate = useNavigate();

  const handleClick = (): void => {
    navigate(`/insights/${insight.id}`);
  };

  const truncateDescription = (
    text: string | undefined,
    maxLength: number = DEFAULT_DESCRIPTION_MAX_LENGTH
  ): string => {
    if (!text) return '';
    if (text.length <= maxLength) return text;

    return text.substring(0, maxLength) + '...';
  };

  return (
    <button
      onClick={handleClick}
      className="bg-navy-50 px-5 py-4 rounded-md w-72 min-h-28 flex flex-col justify-between border border-navy-100 text-left cursor-pointer relative"
    >
      {!insight.viewed && (
        <Box
          sx={{
            position: 'absolute',
            top: '0.75rem',
            right: '0.75rem',
            width: '0.5rem',
            height: '0.5rem',
            bgcolor: 'tokens.critical.500',
            borderRadius: '9999px',
          }}
        />
      )}
      <Stack>
        <Box
          sx={{
            fontWeight: 700,
            mb: 0.5,
            textAlign: 'left',
            color: 'tokens.gray.700',
          }}
        >
          {insight.name}
        </Box>

        {insight.description && (
          <p className="text-sm text-gray-700 mb-3">
            {truncateDescription(insight.description)}
          </p>
        )}
      </Stack>

      <Box
        sx={{
          fontSize: '0.75rem',
          lineHeight: '1rem',
          color: 'tokens.gray.600',
          mt: 'auto',
        }}
      >
        <Box sx={{ fontWeight: 600 }}>
          {formatDate(insight.createdDate, 'MMMM D, YYYY')}
        </Box>
        <Box>
          Created by:{' '}
          <span className="font-semibold">
            {insight.createdBy || 'Unknown'}
          </span>
        </Box>
      </Box>
    </button>
  );
};

export default InsightItem;
